import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Check, Lock } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'

export const CheckoutStepConnector = ({ complete }: { complete: boolean }) => (
  <div className="relative flex w-7 shrink-0 items-center self-center">
    <div className="h-0.5 w-full rounded-full bg-gray-200/90" />
    <motion.div
      className="absolute left-0 h-0.5 rounded-full bg-emerald-400"
      initial={false}
      animate={{ width: complete ? '100%' : '16%' }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    />
  </div>
)

export const ProductThumbs = ({
  items,
}: {
  items: Array<{ key: string; url: string }>
}) => (
  <Row className="items-end pl-1">
    {items.map((item, index) => (
      <motion.div
        key={item.key}
        className={cn(
          '-ml-2.5 h-12 w-12 overflow-hidden rounded-2xl border-2 border-white bg-white shadow-sm first:ml-0',
          index === 0 && '-rotate-3',
          index === 1 && 'rotate-2',
          index === 2 && '-rotate-1',
          index === 3 && 'rotate-3'
        )}
        animate={{ y: [0, -3, 0] }}
        transition={{
          duration: 3.6 + index * 0.3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <img src={item.url} alt="" className="h-full w-full object-cover" />
      </motion.div>
    ))}
  </Row>
)

export const CheckoutStep = ({
  stepNumber,
  title,
  description,
  state,
  selected,
  onSelect,
  accessory,
}: {
  stepNumber: number
  title: string
  description?: string | null
  state: 'locked' | 'active' | 'completed'
  selected: boolean
  onSelect: () => void
  accessory?: ReactNode
}) => {
  const isCompleted = state === 'completed'
  const isLocked = state === 'locked'
  const isActive = state === 'active'

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      disabled={isLocked}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.35,
        delay: 0.06 + stepNumber * 0.04,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={cn(
        'relative min-w-0 flex-1 overflow-hidden rounded-[20px] px-5 py-4 text-left',
        'border shadow-soft',
        'transition-[background-color,border-color,box-shadow,transform,opacity]',
        isLocked && 'cursor-not-allowed opacity-55',
        !isLocked &&
          'cursor-pointer hover:-translate-y-0.5 hover:shadow-new-hover active:translate-y-px',
        !isLocked &&
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/40 focus-visible:ring-offset-2',
        selected &&
          isActive &&
          'heartbeat-border border-orange-200 bg-gradient-to-br from-orange-50/80 via-white to-sky-50/40',
        selected &&
          isCompleted &&
          'border-emerald-200 bg-gradient-to-br from-emerald-50/90 via-white to-sky-50/50',
        !selected && isActive && 'border-orange-200 bg-orange-50/40',
        !selected && isCompleted && 'border-emerald-100 bg-white',
        !selected && isLocked && 'border-[var(--raised-border)] bg-white'
      )}
    >
      <Row className="relative items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            isCompleted && 'bg-emerald-500 text-white',
            isActive &&
              'border border-orange-400 bg-white text-orange-700 shadow-sm',
            isLocked && 'border border-gray-200 bg-white text-gray-400'
          )}
        >
          {isCompleted ? (
            <motion.span
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 380, damping: 18 }}
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
            </motion.span>
          ) : (
            stepNumber
          )}
        </div>

        <Column className="min-w-0 flex-1 gap-1">
          <span className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-[#1A1C1E]">
            {title}
          </span>
          {description ? (
            <span className="text-[13px] leading-[1.45] text-[#6B7280]">
              {description}
            </span>
          ) : null}
        </Column>

        {accessory}

        {isLocked ? (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-500">
            <Lock className="h-4 w-4" strokeWidth={2.2} />
          </div>
        ) : null}
      </Row>
    </motion.button>
  )
}
